import type { Meta, ReceivedAction } from 'redux-first-router';
import { actionToPath, pathToAction } from 'redux-first-router';
import queryString from 'query-string';
import * as RouteConstants from '@/constants/routes';
import routesGenerator from './routesGenerator';
import type { Locale } from '@/utils/locale';
import {
  ROUTE_COMPANY,
  ROUTE_COMPANY_WITH_EVENTS,
  ROUTE_LOCALE_COMPANY,
  ROUTE_LOCALE_COMPANY_WITH_EVENTS,
} from '@/constants/routes';

type _RouteActionType<
  R extends keyof typeof RouteConstants = keyof typeof RouteConstants,
> = R extends `ROUTE_${string}` ? (typeof RouteConstants)[R] : never;

/**
 * All navigable raw route action types in the app.
 */
export type RouteActionType = Prettify<_RouteActionType>;

/**
 * An action produced for Redux First Router.
 */
export type RouteAction = Prettify<{
  type: _RouteActionType;
  payload: {
    query?: {
      [key: string]: string | undefined;
    };
    locale?: Locale;
    [key: string]: any;
  };
  meta: Meta;
  query?: {
    [key: string]: string | undefined;
  };
}>;

const ROUTE_ACTION_TYPES = (() => {
  const keys = Object.keys(RouteConstants).filter((key) =>
    key.startsWith('ROUTE_')
  );
  return keys.map((key) => RouteConstants[key]) as RouteActionType[];
})();

/**
 * Whether the provided action type is a valid action type for Redux First Router.
 */
export const isRouteActionType = (
  actionType: string
): actionType is RouteActionType => {
  return ROUTE_ACTION_TYPES.includes(actionType as RouteActionType);
};

/**
 * Whether the provided action is an action produced for Redux First Router.
 */
export const isRouteAction = (
  action: ReceivedAction
): action is RouteAction => {
  return isRouteActionType(action.type as RouteActionType);
};

const routesMap = routesGenerator();

/**
 * Handle company report as a special case, as they specifically do not handle locale routes in the
 * same way as other pages. This is due to the canonical URL being used directly out of the
 * redux location pathname in some instances.
 */
export const isCompanyActionType = (type: string) =>
  type === ROUTE_COMPANY ||
  type === ROUTE_COMPANY_WITH_EVENTS ||
  type === ROUTE_LOCALE_COMPANY ||
  type === ROUTE_LOCALE_COMPANY_WITH_EVENTS;
export const isLocaleActionType = (type: string) => type.includes('-LOCALE');
export const convertToLocaleActionType = (type: string) =>
  !isLocaleActionType(type) ? `${type}-LOCALE` : type;
export const convertToNonLocaleActionType = (type: string) =>
  type.replace('-LOCALE', '');

export const getPath = (action: ReceivedAction) => {
  // Using a non-route action type will give error responses, so avoid converting it to a path.
  if (!isRouteActionType(action.type)) {
    return '';
  }

  let currentLocale = 'en';

  if (typeof window !== 'undefined') {
    const currentRouteAction = pathToAction(
      window.location.pathname,
      routesMap
    );
    currentLocale = currentRouteAction.payload?.locale ?? currentLocale;
  }

  const pathLocale = action.payload?.locale ?? currentLocale;

  // If there is no locale variant of the action, or we don't wish to automatically handle it,
  // return the path as is.
  if (
    (!isLocaleActionType(action.type) &&
      !isRouteActionType(`${action.type}-LOCALE`)) ||
    isCompanyActionType(action.type)
  ) {
    return actionToPath(action, routesMap, queryString);
  }

  // Handle going from `en` locale to another locale or from a non-locale action to a locale action.
  // The `en` locale is the default action as it has a route without the locale included in it.
  // When the locale is changed, we need to update the route to include the new locale, regardless
  // of whether the action is a locale action or not.
  if (!isLocaleActionType(action.type) && pathLocale !== 'en') {
    action.type = convertToLocaleActionType(action.type);
    action.payload.locale = pathLocale;
    return actionToPath({ ...action }, routesMap, queryString);
  }

  // Handle going to from a non-'en' locale to the 'en' locale.
  if (isLocaleActionType(action.type) && action.payload?.locale === 'en') {
    action.type = convertToNonLocaleActionType(action.type);
    return actionToPath({ ...action }, routesMap, queryString);
  }

  // Ensure a locale is set in the action if it's not already set.
  if (isLocaleActionType(action.type) && !action.payload.locale) {
    action.payload.locale = pathLocale;
  }

  return actionToPath(action, routesMap, queryString);
};

export const getPathSafe = (action: ReceivedAction) => {
  try {
    return getPath(action);
  } catch (error) {
    console.error('Unable to generate path', error);
    return null;
  }
};

export const getCurrentRouteObject = () => {
  const action = pathToAction(window.location.pathname, routesGenerator());
  return routesMap[action.type];
};

export const isAuthenticationNeeded = (routeName: string) =>
  Boolean(routesMap[routeName]?.needsAuthentication);

export const isCacheable = (action: ReceivedAction) =>
  Boolean(routesMap[action.type]?.cacheable);

export type CacheKeyParams = {
  pathname: string;
  query: Record<string, string | undefined>;
};
/**
 * Function to generate a cache key for the route. If not provided, the default cache key will be used.
 * This cache key is used to differentiate between different routes with the same path but different query parameters.
 */
export type RouteCacheKeyFn = (params: CacheKeyParams) => string;

const defaultCacheKey: RouteCacheKeyFn = ({ query, pathname }) => {
  const { page } = query;
  const pageQuery =
    typeof page !== 'undefined' && page !== '1' ? `/page/${page}` : '';

  return `${pathname}${pageQuery}`;
};

export const getCacheIdByRoute = (
  action: ReceivedAction,
  params: CacheKeyParams
) => {
  const cacheKeyFn = routesMap[action.type]?.cacheKey;
  return typeof cacheKeyFn === 'function'
    ? cacheKeyFn(params)
    : defaultCacheKey(params);
};
